'use client';

import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import blogPosts from '../blogs';
import besticles from '../besticles';
import reviews from '../reviews';
import heavenpediaEntries from '../heavenpedia';
import { filterValidBlogPosts, filterValidBesticles, filterValidReviews } from '../utils/contentFilters';

// Define the search result types
export interface SearchResult {
  id: string | number;
  title: string;
  excerpt: string;
  slug: string;
  category: string;
  image?: string;
  type: 'blog' | 'best' | 'reviews' | 'heavenpedia';
  url: string;
}

// Define the context type
interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;
  performSearch: (query: string) => void;
}

// Create the context with default values
export const SearchContext = createContext<SearchContextType>({
  searchQuery: '',
  setSearchQuery: () => {},
  searchResults: [],
  isSearching: false,
  isSearchOpen: false,
  setIsSearchOpen: () => {},
  showSuggestions: false,
  setShowSuggestions: () => {},
  performSearch: () => {},
});

// Custom hook to use the search context
export const useSearch = () => useContext(SearchContext);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Function to perform search
  const performSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Filter content using the same logic as archive pages
    const validBlogPosts = filterValidBlogPosts(blogPosts);
    const validBesticles = filterValidBesticles(besticles);
    const validReviews = filterValidReviews(reviews);
    
    // Search in blog posts
    const blogResults = validBlogPosts.filter(post => 
      post.title.toLowerCase().includes(query.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      post.category.toLowerCase().includes(query.toLowerCase())
    ).map(post => ({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt,
      slug: post.slug,
      category: post.category,
      image: post.image,
      type: 'blog' as const,
      url: `/blog/${post.slug}`
    }));

    // Search in "best" posts
    const bestResults = validBesticles.filter(post => 
      post.title.toLowerCase().includes(query.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      post.category.toLowerCase().includes(query.toLowerCase())
    ).map(post => ({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt,
      slug: post.slug,
      category: post.category,
      image: post.image,
      type: 'best' as const,
      url: `/best/${post.slug}`
    }));

    // Search in review posts
    const reviewResults = validReviews.filter(review => 
      review.title.toLowerCase().includes(query.toLowerCase()) ||
      review.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      review.category.toLowerCase().includes(query.toLowerCase())
    ).map(review => ({
      id: review.id,
      title: review.title,
      excerpt: review.excerpt,
      slug: review.slug,
      category: review.category,
      image: review.image,
      type: 'reviews' as const,
      url: `/reviews/${review.slug}`
    }));

    // Search in heavenpedia entries
    const heavenpediaResults = heavenpediaEntries.filter(entry => 
      entry.title.toLowerCase().includes(query.toLowerCase()) ||
      entry.excerpt.toLowerCase().includes(query.toLowerCase())
    ).map(entry => ({
      id: entry.id,
      title: entry.title,
      excerpt: entry.excerpt,
      slug: entry.slug,
      category: 'Heavenpedia',
      image: entry.image,
      type: 'heavenpedia' as const,
      url: `/heavenpedia/${entry.slug}`
    }));

    // Combine and sort results
    const combinedResults = [...blogResults, ...bestResults, ...reviewResults, ...heavenpediaResults].sort((a, b) => {
      // Prioritize exact title matches
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      const queryLower = query.toLowerCase();
      
      if (aTitle === queryLower && bTitle !== queryLower) return -1;
      if (bTitle === queryLower && aTitle !== queryLower) return 1;
      
      // Then prioritize by how early the query appears in the title
      return aTitle.indexOf(queryLower) - bTitle.indexOf(queryLower);
    });

    setSearchResults(combinedResults);
    setIsSearching(false);
  };

  // Add keyboard shortcut listener (/ key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only activate if not in an input or textarea
      if (e.key === '/' && 
          document.activeElement?.tagName !== 'INPUT' && 
          document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      
      // Close search on escape
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setShowSuggestions(false);
      }
    };

    // Remove any existing listeners first (important for StrictMode)
    document.removeEventListener('keydown', handleKeyDown);
    
    // Add the new listener
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      // Ensure cleanup happens
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Effect to perform search when query changes
  useEffect(() => {
    if (searchQuery) {
      performSearch(searchQuery);
      setShowSuggestions(true);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        searchResults,
        isSearching,
        isSearchOpen,
        setIsSearchOpen,
        showSuggestions,
        setShowSuggestions,
        performSearch
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
